import path from 'path';

import { OffsetPagedResults } from '~interfaces';
import { BasePagedParameters } from '~interfaces/requests';
import { PagedResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

class RevenuesService extends TopologyService {
	public readonly path = 'finances/revenues';
	public readonly currentSubPath = 'current';
	public readonly businessSubPath = 'business';
	public readonly personalSubPath = 'personal';

	constructor() {
		super('v1');
	}

	/**
	 * Get the current revenue unmapped
	 */
	async getCurrentRevenueUnmapped(excludeOneOffPayments: boolean): Promise<RevenueResponse> {
		const { data } = await this._client.get<RevenueResponse>(
			path.join(this.path, this.currentSubPath),
			{
				params: {
					excludeOneOffPayments: excludeOneOffPayments,
				},
			}
		);

		return data;
	}

	/**
	 * Get the current revenue unmapped
	 */
	async getRevenuesUnmapped(
		args: BasePagedParameters & { excludeOneOffPayments: boolean }
	): Promise<OffsetPagedResults<RevenueResponse>> {
		const { data } = await this._client.get<PagedResponse<RevenueResponse>>(this.path, {
			params: {
				pageNumber: args.page,
				pageSize: args.pageSize,
				excludeOneOffPayments: args.excludeOneOffPayments,
			},
		});

		return this.mapPagedResponse(data, (el) => el);
	}

	/**
	 * Get the business and/or personal bookings. Do notice that this endpoint
	 * return inconsistent data. Depending on the configuration it is used to
	 * show personal revenue or business revenue.
	 * E.g. billings for the partners is pure business revenue
	 * @param args
	 */
	async getBusinessRevenuesUnmapped(
		date: string, // TODO: eventually Date type
		args: BasePagedParameters & { searchTerm?: string; excludeOneOffPayments: boolean }
	): Promise<RevenueResponse> {
		const { data } = await this._client.get<RevenueResponse>(
			path.join(this.path, date, this.businessSubPath),
			{
				params: {
					pageNumber: args.page,
					pageSize: args.pageSize,
					organisationId: args.organisationId,
					searchTerm: args.searchTerm,
					excludeOneOffPayments: args.excludeOneOffPayments,
				},
			}
		);

		return data;
	}

	/**
	 * Get the personal revenues
	 * @param args
	 */
	async getPersonalRevenuesUnmapped(
		date: string, // TODO: eventually Date type
		args: BasePagedParameters & { excludeOneOffPayments: boolean }
	): Promise<RevenueResponse> {
		const { data } = await this._client.get<RevenueResponse>(
			path.join(this.path, date, this.personalSubPath),
			{
				params: {
					pageNumber: args.page,
					pageSize: args.pageSize,
					organisationId: args.organisationId,
					excludeOneOffPayments: args.excludeOneOffPayments,
				},
			}
		);

		return data;
	}
}

interface RevenueResponse {
	start: string;
	end: string;
	title: string;
	open: number;
	status: string;
	billings: object[];
	amount: {
		currencySymbol: string;
		subtotal: number;
		discount: number;
		refund: number;
		priceVat: number;
		totalPrice: number;
	};
}

export default RevenuesService;
